
"use client";

import React, { createContext, useContext, ReactNode, useMemo, useState, useEffect, useCallback } from 'react';

interface KioskModeContextType {
  isKioskMode: boolean;
  enterKioskMode: () => void;
  exitKioskMode: () => void;
}

const KioskModeContext = createContext<KioskModeContextType>({
  isKioskMode: false,
  enterKioskMode: () => {},
  exitKioskMode: () => {},
});

export const useKioskMode = () => useContext(KioskModeContext);

const KIOSK_MODE_SESSION_KEY = 'swiftserve-kioskMode';

export const KioskModeProvider = ({ children }: { children: ReactNode }) => {
  const [isKioskMode, setIsKioskMode] = useState(false);

  // This effect runs once on mount to check sessionStorage and set the initial state.
  useEffect(() => {
    const kioskModeFromStorage = sessionStorage.getItem(KIOSK_MODE_SESSION_KEY) === 'true';
    if (kioskModeFromStorage) {
      setIsKioskMode(true);
    }
  }, []);


  const enterKioskMode = useCallback(() => {
    sessionStorage.setItem(KIOSK_MODE_SESSION_KEY, 'true');
    setIsKioskMode(true);
  }, []);

  const exitKioskMode = useCallback(() => {
    sessionStorage.removeItem(KIOSK_MODE_SESSION_KEY);
    setIsKioskMode(false);
  }, []);

  const value = useMemo(() => ({
    isKioskMode,
    enterKioskMode,
    exitKioskMode,
  }), [isKioskMode, enterKioskMode, exitKioskMode]);

  return <KioskModeContext.Provider value={value}>{children}</KioskModeContext.Provider>;
};
